import React from 'react';
import StatusCodeSelect from 'components/transactionEditor/StatusCodeSelect';
import { HttpStatusCodeItem } from 'utils/httpStatusCodes';
import Tabs from 'react-bootstrap/Tabs';
import { Form, Tab } from 'react-bootstrap';
import ResponseBody from 'components/transactionEditor/ResponseBody';
import _ from 'lodash';
import ResponseKeyValueList, { ResponseKeyValueItem } from 'components/transactionEditor/ResponseKeyValueList';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { RequestTabState, title } from '../RequestTab/RequestTab';
import i18n from 'i18n';
import ParameterOptions from 'components/ui/hyperLink/ParameterOptions';
import { ContentType, FaultType, StatusCodeCondition } from 'types/rest/dslTypes';
import { AssetAssignment, DelayType, ResponseDelay } from 'types/rest/mockServiceTypes';
import RedirectToLive from '../RedirectToLive/RedirectToLive';
import FaultTab from './FaultTab/FaultTab';
import ProxySetting from './ProxySetting';
import ConditionalStatusCodes from './ConditionalStatusCodes/ConditionalStatusCodes';
import ThinkTimeRadioGroup from 'views/MockServices/Tabs/MockServices/Params/ThinkTimeRadioGroup';
import { AssetAssignmentBundle } from '../../../../MockServices/Tabs/MockServices/SslConfiguration';
import { TransactionType } from '../../../../../types/rest/assetCatalogTypes';

interface ResponseTabProps {
  state: ResponseTabState;
  requestTabState: RequestTabState;
  updateState: (state: ResponseTabState) => void;
  isReadOnly?: boolean;
  isResponseBodyMagicStringInvalid?: boolean;
  isResponseHeaderMagicStringInvalid?: boolean;
  resBodyInvalidMagicStrings?: string;
  resHeaderInvalidMagicStrings?: string;
  activeResponseTab: string;
  activeResponseTabChanged: (activeTab: string) => void;
  isWizardModal?: boolean;
  type: TransactionType;
  serviceId?: number;
  insideSandBox?: boolean;
  transactionId?: number;
}

export interface ResponseTabState {
  status: HttpStatusCodeItem;
  statusCodeConditions: StatusCodeCondition[];
  proxyUrl: string;
  headers: ResponseKeyValueItem[];
  contentType: ContentType;
  content: string;
  binary: boolean;
  dirty: boolean;
  responseDelay: ResponseDelay;
  isValid: boolean;
  redirectUrl?: string;
  certificateId?: number;
  failoverEnabled: boolean;
  assets?: AssetAssignment[];
  configKeyAssets?: AssetAssignment[];
  fault?: FaultType;
  [key: string]:
    | string
    | number
    | StatusCodeCondition[]
    | HttpStatusCodeItem
    | ResponseKeyValueItem[]
    | boolean
    | null
    | ResponseDelay
    | AssetAssignment[]
    | undefined;
}

const ResponseTab = (props: ResponseTabProps) => {
  const updateState = (field: string, value: any) => {
    const updatedState = _.cloneDeep(props.state);
    _.set(updatedState, field, value);
    updatedState.dirty = true;
    props.updateState(updatedState);
  };

  const updateAssetBundleHandler = (assetBundle: AssetAssignmentBundle) => {
    const updatedState = _.cloneDeep(props.state);
    updatedState.assets = assetBundle.assets;
    updatedState.configKeyAssets = assetBundle.configKeyAssets;
    props.updateState(updatedState);
  };

  const handleFieldEvent = (e: any) => {
    const target = e.target as HTMLInputElement;
    updateState(target.name, target.value);
  };

  const updateResponseDelay = (value: ResponseDelay) => {
    const updatedState = _.cloneDeep(props.state);
    _.set(updatedState, 'responseDelay', value);
    _.set(updatedState, 'isValid', true);
    updatedState.dirty = true;
    props.updateState(updatedState);
  };

  const isThinkTimeSet = (): boolean => {
    return !_.isNull(props.state.responseDelay) && props.state.responseDelay?.type !== DelayType.NONE;
  };

  const isRedirectSet = (): boolean => {
    return !_.isNil(props.state.redirectUrl);
  };

  return (
    <div className="bg-light p-4">
      <Row className="mb-4">
        <Col md={12}>
          {props.isWizardModal === true ? <>{i18n.t('wizard:details.behavior.response')}</> : <ParameterOptions />}
        </Col>
      </Row>
      <Form.Label htmlFor="react-select-3-input">
        {_.isEmpty(props.state.statusCodeConditions)
          ? i18n.t('transactions:txn.statusCode')
          : 'Fallback ' + i18n.t('transactions:txn.statusCode')}
      </Form.Label>
      <Row className="mb-3">
        <Col md={4}>
          <StatusCodeSelect
            isReadOnly={props.isReadOnly}
            onSelect={(item: HttpStatusCodeItem) => updateState('status', item)}
            selectedStatus={props.state.status}
            id="qa-select-status-code"
          />
          {props.isWizardModal === true && (
            <div className="text-muted small mt-2">{i18n.t('wizard:details.behavior.statusCodeDescription')}</div>
          )}
        </Col>
      </Row>

      {/* Conditional Status Codes */}
      <div className={'mb-4'}>
        <ConditionalStatusCodes
          conditionalStatusCodes={props.state.statusCodeConditions}
          isDisabled={props.isReadOnly}
          onUpdate={conditionalStatusCodes => {
            updateState('statusCodeConditions', conditionalStatusCodes);
          }}
        />
      </div>

      {props.isWizardModal !== true ? (
        <Tabs
          id="qa-tabs-response"
          activeKey={props.activeResponseTab}
          onSelect={(key: string) => props.activeResponseTabChanged(key)}
        >
          <Tab title={i18n.t('transactions:editor.bodyTitle')} eventKey="body">
            <ResponseBody
              isReadOnly={props.isReadOnly}
              onChange={updateState}
              binary={props.state.binary}
              content={props.state.content}
              isResponseBodyMagicStringInvalid={props.isResponseBodyMagicStringInvalid}
              resBodyInvalidMagicStrings={props.resBodyInvalidMagicStrings}
              requestTabState={props.requestTabState}
              serviceId={props.serviceId}
              enableAiAssistant={true}
            />
          </Tab>
          <Tab
            title={title(i18n.t('transactions:editor.responseHeaders'), props.state.headers.length)}
            eventKey="response-headers"
          >
            <ResponseKeyValueList
              isReadOnly={props.isReadOnly}
              id="response-header-list"
              responseItemList={props.state.headers}
              emptyListMessage={i18n.t('transactions:editor.noResponseHeaders')}
              onListChange={list => updateState('headers', list)}
              resHeaderInvalidMagicStrings={props.resHeaderInvalidMagicStrings}
              isResponseHeaderMagicStringInvalid={props.isResponseHeaderMagicStringInvalid}
              enableAiAssistant={true}
              transactionId={props.transactionId}
              multilineValue={true}
            />
          </Tab>
          {!_.isNil(props.state.proxyUrl) && props.insideSandBox !== true && (
            <Tab
              title={title(
                i18n.t('transactions:editor.proxyUrl'),
                props.state.proxyUrl
                  ? `${i18n.t('transactions:editor.defined')}`
                  : `${i18n.t('transactions:editor.notDefined')}`,
              )}
              eventKey="proxyUrl"
            >
              <Row>
                <Col md={4}>
                  <ProxySetting
                    onRemove={() => {
                      updateState('proxyUrl', '');
                    }}
                    value={props.state.proxyUrl}
                    onChange={handleFieldEvent}
                    isReadOnly={true}
                  />
                </Col>
              </Row>
            </Tab>
          )}
          {props.insideSandBox !== true && (
            <Tab
              title={title(
                i18n.t('common:thinkTime.title'),
                isThinkTimeSet()
                  ? `${i18n.t('transactions:editor.defined')}`
                  : `${i18n.t('transactions:editor.notDefined')}`,
              )}
              eventKey="response-thinkTime"
            >
              <Col md={3}>
                <ThinkTimeRadioGroup
                  responseDelay={props.state.responseDelay}
                  updateResponseDelay={updateResponseDelay}
                  updateState={updateState}
                  noDelayText={i18n.t('common:thinkTime.txNoDelay')}
                />
              </Col>
            </Tab>
          )}

          <Tab
            title={title(
              i18n.t('transactions:redirectToLive.title'),
              isRedirectSet()
                ? `${i18n.t('transactions:editor.defined')}`
                : `${i18n.t('transactions:editor.notDefined')}`,
            )}
            eventKey="response-redirectToLive"
          >
            <RedirectToLive
              redirectUrlChanged={redirectUrl => updateState('redirectUrl', redirectUrl)}
              redirectUrl={props.state.redirectUrl}
              assetBundle={{ assets: props.state.assets, configKeyAssets: props.state.configKeyAssets }}
              failoverEnabled={props.state.failoverEnabled}
              assetsChanged={assetBundle => updateAssetBundleHandler(assetBundle)}
              setValidity={isValid => updateState('isValid', isValid)}
              onFailoverChanged={failoverEnabled => updateState('failoverEnabled', failoverEnabled)}
            />
          </Tab>
          {props.type !== 'MESSAGING' && (
            <Tab
              title={title(
                i18n.t('transactions:faults.title'),
                props.state.fault
                  ? `${i18n.t('transactions:editor.defined')}`
                  : `${i18n.t('transactions:editor.notDefined')}`,
              )}
              eventKey="response-faults"
            >
              <FaultTab
                fault={props.state.fault}
                type={props.type}
                isReadOnly={props.isReadOnly}
                onFaultChanged={fault => updateState('fault', fault)}
              />
            </Tab>
          )}
        </Tabs>
      ) : (
        <ResponseBody
          isReadOnly={props.isReadOnly}
          onChange={updateState}
          binary={props.state.binary}
          content={props.state.content}
          hideWidgets={true}
          requestTabState={props.requestTabState}
          serviceId={props.serviceId}
        />
      )}
    </div>
  );
};

export default ResponseTab;
